"use client";

import { type SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown } from "lucide-react";
import { Icon } from "@iconify-icon/react";

import timezones from "./timezones";

import availableLanguages from "@/i18n/availableLanguages";
import { sendPreferencesData } from "@/features/auth/api/onboarding";
import useAuthStore from "@/features/auth/store/useAuthStore";
import errorHandler, { assignApiErrorsToFormFields } from "@/features/auth/utils/errorHandler";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

interface PreferencesFormProps {
  goToNextStep: () => void;
  goToPrevStep: () => void;
}

const preferencesFormSchema = z.object({
  language: z.preprocess((val) => {
    return val === "" ? undefined : val;
  }, z.string()),
  timezone: z.preprocess((val) => {
    return val === "" ? undefined : val;
  }, z.string()),
});

type PreferencesFormValues = z.infer<typeof preferencesFormSchema>;

const PreferencesForm = ({ goToNextStep, goToPrevStep }: PreferencesFormProps) => {
  const { i18n, t } = useTranslation(["forms", "common", "auth", "timezones"]);
  const setOnboardingData = useAuthStore((state) => state.setOnboardingData);

  const form = useForm<PreferencesFormValues>({
    resolver: zodResolver(preferencesFormSchema),
    defaultValues: {
      language: i18n.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    setError,
  } = form;

  const onSubmit: SubmitHandler<PreferencesFormValues> = async ({ language, timezone }) => {
    try {
      const res = await sendPreferencesData({ locale: language.toUpperCase(), timezone });

      setOnboardingData({ language, timezone });

      console.log(res);
      goToNextStep();
    } catch (error) {
      const apiErrors = errorHandler<"locale" | "timezone">(error);

      assignApiErrorsToFormFields(apiErrors, setError, {
        language: "locale",
        timezone: "timezone",
      });
    }
  };

  const languageValue = watch("language");

  return (
    <Form {...form}>
      <form className="grid gap-12" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-7">
          <div>
            <FormLabel className={"block w-full! text-center!"}>
              {t("auth:signUp.defaultLanguage")}
              <span className="text-destructive ml-1">*</span>
            </FormLabel>
            <div className="mt-4 flex justify-center gap-6">
              {availableLanguages.map(({ language, flagImg, name }) => {
                const isSelected = languageValue === language;

                return (
                  <Button
                    key={language}
                    className={cn(
                      "bg-muted/20 border-border/50 hover:bg-muted/30 h-16 w-16 p-3 shadow-lg backdrop-blur-sm transition-all duration-200",
                      isSelected
                        ? "border-accent-foreground/40 bg-accent-foreground/10"
                        : errors.language && !languageValue
                          ? "border-destructive"
                          : "opacity-70",
                    )}
                    size="lg"
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setValue("language", language);
                    }}
                  >
                    <img
                      alt={name}
                      className="h-full w-full rounded object-cover"
                      src={flagImg || "/placeholder.svg"}
                    />
                  </Button>
                );
              })}
            </div>
            {errors.language && !languageValue ? (
              <p className="text-small text-danger mt-2">{errors.language.message}</p>
            ) : null}
          </div>
          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>
                  {t("auth:signUp.timezone")}
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        className={cn(
                          "bg-muted/20 border-border/50 hover:bg-muted/30 justify-between backdrop-blur-sm transition-all duration-200",
                          !field.value && "text-muted-foreground",
                        )}
                        role="combobox"
                        variant="outline"
                      >
                        <div className="flex flex-row items-center gap-2">
                          <span className="iconify tabler--clock-filled" />
                          {field.value
                            ? t(
                                //@ts-ignore
                                `timezones:${timezones.find((timezone) => timezone === field.value)}`,
                              )
                            : t("auth:signUp.selectTimezone")}
                        </div>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="p-0">
                    <Command>
                      <CommandInput placeholder={t("auth:signUp.searchTimezone")} />
                      <CommandList>
                        <CommandEmpty>{t("auth:signUp.noTimezoneFound")}</CommandEmpty>
                        <CommandGroup>
                          {timezones.map((timezone) => (
                            <CommandItem
                              key={timezone}
                              className={"text-accent-foreground"}
                              value={t(
                                //@ts-ignore
                                `timezones:${timezone}`,
                              )}
                              onSelect={() => {
                                form.setValue("timezone", timezone);
                              }}
                            >
                              {t(
                                //@ts-ignore
                                `timezones:${timezone}`,
                              )}
                              <Check
                                className={cn(
                                  "ml-auto",
                                  timezone === field.value ? "opacity-100" : "opacity-0",
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid gap-4">
          <div className={"flex w-full gap-2"}>
            <Button
              className={
                "bg-muted/20 hover:bg-muted/30 border-border/50 w-20 shadow-lg backdrop-blur-sm"
              }
              disabled={isSubmitting}
              size="lg"
              type="button"
              variant="outline"
              onClick={goToPrevStep}
            >
              <Icon icon={"mdi:arrow-left"} />
              {t("common:back")}
            </Button>
            <Button
              className={
                "bg-accent-foreground/90 hover:bg-accent-foreground text-background w-full shadow-xl"
              }
              disabled={isSubmitting || !form.formState.isValid}
              size="lg"
              type="submit"
            >
              {t("common:continue")}
              <Icon icon={"mdi:arrow-right"} />
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default PreferencesForm;
